import * as React from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import {
	DataGrid,
	GridActionsCellItem,
	GridRowModes,
	GridRowEditStopReasons,
} from "@mui/x-data-grid";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";

let idCounter = 1;

function randomId() {
	return idCounter++;
}

// const initialRows = [];

function EditToolbar({ setRows, setRowModesModel }) {
	const handleClick = () => {
		const id = randomId();
		setRows((prevRows) => [
			...prevRows,
			{
				id,
				insuredName: "",
				insuredContact: "",
				isPrimary: false,
				isNew: true,
			},
		]);
		setRowModesModel((prevModel) => ({
			...prevModel,
			[id]: { mode: GridRowModes.Edit, fieldToFocus: "insuredName" },
		}));
	};

	return (
		<Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
			<Tooltip title="Add Insured">
				<Button
					variant="contained"
					color="primary"
					startIcon={<AddIcon />}
					onClick={handleClick}
				>
					Add Insured
				</Button>
			</Tooltip>
		</Box>
	);
}

export default function InsuredCrudGrid({ value = [], onChange }) {
	const [rows, setRows] = React.useState([]);
	const [originalRows, setOriginalRows] = React.useState([]);
	const [deletedRows, setDeletedRows] = React.useState([]);
	const [rowModesModel, setRowModesModel] = React.useState({});

	// Initialize rows from parent (for edit mode)
	React.useEffect(() => {
		if (Array.isArray(value) && value.length > 0) {
			const initialRows = value.map((row) => ({
				...row,
				isNew: false,
			}));
			
			setRows(initialRows);
			setOriginalRows(initialRows.map((row) => ({ ...row })));
			setDeletedRows([]);
			setRowModesModel({});
		} else {
			setRows([]);
			setOriginalRows([]);
			setDeletedRows([]);
		}
	}, [value]);

	// Compute changes and notify parent
	React.useEffect(() => {
		if (onChange) {
			// Added: rows with isNew
			const added = rows.filter((r) => r.isNew);
			// Updated: rows that existed before and have changed
			const updated = rows.filter((r) => {
				if (r.isNew) return false;
				const orig = originalRows.find((o) => o.id === r.id);
				return (
					orig &&
					(orig.insuredName !== r.insuredName ||
						orig.insuredContact !== r.insuredContact ||
						orig.isPrimary !== r.isPrimary)
				);
			});
			// Deleted: rows in deletedRows
			const deleted = deletedRows;
			onChange({ added, updated, deleted });
		}
	}, [rows, deletedRows, originalRows, onChange]);

	const handleRowEditStop = (params, event) => {
		if (params.reason === GridRowEditStopReasons.rowFocusOut) {
			event.defaultMuiPrevented = true;
		}
	};

	const handleEditClick = (id) => () => {
		setRowModesModel({
			...rowModesModel,
			[id]: { mode: GridRowModes.Edit },
		});
	};

	const handleSaveClick = (id) => () => {
		setRowModesModel({
			...rowModesModel,
			[id]: { mode: GridRowModes.View },
		});
	};

	const handleDeleteClick = (id) => () => {
		setRows((prevRows) => {
			const row = prevRows.find((r) => r.id === id);
			if (row && !row.isNew) {
				setDeletedRows((prev) => [...prev, row]);
			}
			return prevRows.filter((r) => r.id !== id);
		});
	};

	const handleCancelClick = (id) => () => {
		setRowModesModel({
			...rowModesModel,
			[id]: { mode: GridRowModes.View, ignoreModifications: true },
		});

		const editedRow = rows.find((row) => row.id === id);
		if (editedRow?.isNew) {
			setRows(rows.filter((row) => row.id !== id));
		} else {
			// revert to original
			const orig = originalRows.find((o) => o.id === id);
			if (orig) {
				setRows(rows.map((r) => (r.id === id ? { ...orig } : r)));
			}
		}
	};
	const processRowUpdate = (newRow) => {
		// Validate insured name
		if (!newRow.insuredName.trim()) {
			throw new Error("Insured name is required");
		}

		// Validate phone number format (9-11 digits)
		const cleanedPhone = newRow.insuredContact.replace(/\D/g, '');
		
		if (cleanedPhone.length < 9 || cleanedPhone.length > 11) {
			throw new Error("Phone number must be between 9-11 digits");
		}
		
		// Format phone number with +6 prefix if it starts with 0
		let formattedPhone = cleanedPhone;
		if (cleanedPhone.startsWith('0')) {
			formattedPhone = '+6' + cleanedPhone;
		}
		
		// If this row is being set as primary, unset others
		const updatedRow = { 
			...newRow, 
			insuredContact: formattedPhone,
			isNew: !!newRow.isNew 
		};
		
		if (newRow.isPrimary) {
			setRows((prevRows) =>
				prevRows.map((row) =>
					row.id === newRow.id
						? updatedRow
						: { ...row, isPrimary: false }
				)
			);
		} else {
			setRows((prevRows) =>
				prevRows.map((row) => (row.id === newRow.id ? updatedRow : row))
			);
		}
		return updatedRow;
	};

	const columns = [
		{
			field: "insuredName",
			headerName: "Insured Name",
			width: 500,
			editable: true,
		},
		{
			field: "insuredContact",
			headerName: "Contact Details",
			width: 350,
			editable: true,
		},
		{
			field: "isPrimary",
			headerName: "Is Primary",
			type: "boolean",
			width: 150,
			editable: true,
			renderCell: (params) => (
				<Checkbox checked={params.value} disabled />
			),
		},
		{
			field: "actions",
			type: "actions",
			headerName: "Actions",
			width: 150,
			cellClassName: "actions",
			getActions: ({ id }) => {
				const isInEditMode =
					rowModesModel[id]?.mode === GridRowModes.Edit;
				if (isInEditMode) {
					return [
						<GridActionsCellItem
							key="save"
							icon={<SaveIcon />}
							label="Save"
							onClick={handleSaveClick(id)}
						/>,
						<GridActionsCellItem
							key="cancel"
							icon={<CancelIcon />}
							label="Cancel"
							onClick={handleCancelClick(id)}
							color="inherit"
						/>,
					];
				}

				return [
					<GridActionsCellItem
						key="edit"
						icon={<EditIcon />}
						label="Edit"
						onClick={handleEditClick(id)}
						color="inherit"
					/>,
					<GridActionsCellItem
						key="delete"
						icon={<DeleteIcon />}
						label="Delete"
						onClick={handleDeleteClick(id)}
						color="inherit"
					/>,
				];
			},
		},
	];
	const [errorMessage, setErrorMessage] = React.useState("");

	const handleProcessRowUpdateError = React.useCallback((error) => {
		setErrorMessage(error.message || "An error occurred while saving");
		setTimeout(() => setErrorMessage(""), 5000); // Clear error after 5 seconds
	}, []);

	return (
		<Box sx={{ height: 350, width: "100%" }}>
			{errorMessage && (
				<Box 
					sx={{ 
						p: 1, 
						mb: 1, 
						color: 'error.main',
						backgroundColor: 'error.light',
						borderRadius: 1
					}}
				>
					{errorMessage}
				</Box>
			)}
			<DataGrid
				rows={rows}
				columns={columns}
				editMode="row"
				rowModesModel={rowModesModel}
				onRowModesModelChange={setRowModesModel}
				onRowEditStop={handleRowEditStop}
				processRowUpdate={processRowUpdate}
				onProcessRowUpdateError={handleProcessRowUpdateError}
				slots={{ toolbar: EditToolbar }}
				slotProps={{
					toolbar: { setRows, setRowModesModel },
				}}
			/>
		</Box>
	);
}
